import { getBaseRole } from '@/helpers/customRoles';
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { API, maxBindingApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import { refreshMessengerBindingStatus } from '@/store/messengerBindingSlice'
import {
  hide as hideConfirm,
  show as showConfirm,
  update as updateConfirm,
} from '@store/confirm-reducer'
import { actionUpdProfile, selectProfile } from '@store/profile.reducer'

interface ActivateMaxBotProps {
  open?: boolean
  setOpen?: (open: boolean) => void
  necessarily?: boolean
}

type ConfirmPayload = {
  code: string
  setError: (error: string) => void
}

type MaxGuardMode = 'binding' | 'confirmation' | null

const noopSetOpen = () => {}

const EMPLOYEE_AUTH_OPERATION = 'employee_auth'
const MAX_CODE_LENGTH = 6

const MAX_BINDING_DESCRIPTION =
  'Нажмите «Открыть MAX-бота», перейдите по ссылке, нажмите «Начать» в боте и введите здесь 6-значный код активации.'

const MAX_REQUIRED_BINDING_DESCRIPTION =
  'Для продолжения работы нужно привязать MAX. Нажмите «Открыть MAX-бота», перейдите по ссылке, нажмите «Начать» в боте и введите здесь 6-значный код активации.'

const MAX_LOGIN_CONFIRMATION_DESCRIPTION =
  'Для продолжения работы подтвердите вход через MAX. Перейдите в MAX-бота, получите 6-значный код и введите его здесь.'

const getErrorText = (error: any, fallback: string) => {
  const apiError =
    error?.response?.data?.error ??
    error?.data?.error ??
    error?.response?.data?.message ??
    error?.data?.message

  if (Array.isArray(apiError)) return apiError.join(', ')
  if (typeof apiError === 'string') return apiError

  return fallback
}

const getResponseData = (response: any) => response?.data?.data ?? response?.data

const isEnabledFlag = (value: unknown) =>
  value === true || value === 1 || value === '1'

const isAdminRole = (role: unknown) => getBaseRole(role) === 'admin'

const isMaxHiddenForRole = (role: unknown) =>
  getBaseRole(role) === 'children' || getBaseRole(role) === 'parent'

const isFailedHttpResponse = (response: any) =>
  !response?.status ||
  response.status < 200 ||
  response.status >= 300 ||
  response?.data?.status === 'error'

const reloadPageAfterMaxLoginConfirmation = () => {
  window.location.reload()
}

const getMaxBotUrlFromConfig = async () => {
  const response = await API.get('integration/get-config?name=max', {
    skipGlobalErrorAlert: true,
    skipMaxTwoFactor: true,
  } as any)

  if (isError(response)) return null

  const fields = getResponseData(response)?.fields ?? {}
  const botId = fields?.bot_id

  return botId ? `https://max.ru/${botId}` : null
}

const ActivateMaxBot: FC<ActivateMaxBotProps> = ({
  open = true,
  setOpen = noopSetOpen,
  necessarily = false,
}) => {
  const addAlert = useAddAlert()
  const dispatch = useDispatch()
  const addAlertRef = useRef(addAlert)
  const hasStartedRef = useRef(false)
  const profile = useSelector(selectProfile)
  const userid = profile?.id

  useEffect(() => {
    addAlertRef.current = addAlert
  }, [addAlert])

  useEffect(() => {
    if (!open) {
      hasStartedRef.current = false
      return
    }

    if (!userid || hasStartedRef.current) return

    hasStartedRef.current = true
    setOpen(false)
    dispatch(
      showConfirm({
        variant: 'twoFactor',
        title: necessarily
          ? 'Необходимо активировать MAX-бота'
          : 'Активируйте MAX-бота',
        description: 'Готовим персональную ссылку для привязки MAX...',
        codeLength: MAX_CODE_LENGTH,
        confirmText: 'Активировать',
        cancelText: 'Отменить',
        loading: true,
        onCancel: () => {},
        onConfirm: () => false,
      }),
    )

    maxBindingApi
      .startActivation({
        user_id: userid,
        origin: window.location.origin,
      })
      .then((res) => {
        if (isError(res)) throw res

        const activationUrl = String(getResponseData(res)?.activation_url ?? '')

        dispatch(
          updateConfirm({
            loading: false,
            title: 'Введите код MAX',
            description: activationUrl
              ? MAX_BINDING_DESCRIPTION
              : 'Перейдите в MAX-бота, нажмите «Начать» и введите здесь 6-значный код активации.',
            descriptionLink: activationUrl || null,
            resetCodeKey: Date.now(),
            confirmText: 'Активировать',
            cancelText: 'Отменить',
            onCancel: () => {},
            onConfirm: async ({ code, setError }: ConfirmPayload) => {
              try {
                const activateResponse = await maxBindingApi.activate({
                  user_id: userid,
                  code,
                })

                if (isError(activateResponse)) {
                  setError(
                    getErrorText(
                      activateResponse,
                      'Некорректный код активации или ошибка сервера',
                    ),
                  )
                  return false
                }

                addAlertRef.current({
                  title: 'Успешно',
                  text: 'MAX-бот успешно активирован',
                  type: 'success',
                })
                dispatch(refreshMessengerBindingStatus())

                return true
              } catch (error) {
                setError(
                  getErrorText(
                    error,
                    'Некорректный код активации или ошибка сервера',
                  ),
                )
                return false
              }
            },
          }),
        )
      })
      .catch((error) => {
        dispatch(hideConfirm())
        addAlertRef.current({
          title: 'Ошибка',
          text: getErrorText(
            error,
            'Не удалось подготовить ссылку для активации MAX',
          ),
          type: 'error',
        })
      })
  }, [dispatch, necessarily, open, setOpen, userid])

  return null
}

const RequiredActivateMaxBot = () => {
  const addAlert = useAddAlert()
  const dispatch = useDispatch()
  const addAlertRef = useRef(addAlert)
  const activeModeRef = useRef<MaxGuardMode>(null)
  const showLoginConfirmationModalRef = useRef<
    ((userId: number, canDismiss: boolean) => Promise<void>) | null
  >(null)
  const profile = useSelector(selectProfile)
  const canDismissRequiredMaxModal = isAdminRole(profile?.role)
  const [currentUser, setCurrentUser] = useState<any | null>(null)

  useEffect(() => {
    addAlertRef.current = addAlert
  }, [addAlert])

  const fetchCurrentUser = useCallback(async () => {
    if (!profile?.id) return null

    const response = await API.get(`user/view/${profile.id}`, {
      skipGlobalErrorAlert: true,
    } as any)

    if (isFailedHttpResponse(response)) throw response

    const user = getResponseData(response)

    if (user) {
      setCurrentUser(user)
      dispatch(actionUpdProfile(user))
    }

    return user
  }, [dispatch, profile?.id])

  const showBindingModal = useCallback(
    async (userId: number, canDismiss: boolean) => {
      dispatch(
        showConfirm({
          variant: 'twoFactor',
          title: 'Привяжите MAX',
          description: 'Готовим персональную ссылку для привязки MAX...',
          codeLength: MAX_CODE_LENGTH,
          confirmText: 'Активировать',
          cancelText: 'Отменить',
          loading: true,
          blocking: !canDismiss,
          hideCancel: !canDismiss,
          onCancel: () => {},
          onConfirm: () => false,
        }),
      )

      try {
        const response = await maxBindingApi.startActivation({
          user_id: userId,
          origin: window.location.origin,
        })

        if (isError(response)) throw response

        const activationUrl = String(getResponseData(response)?.activation_url ?? '')

        dispatch(
          updateConfirm({
            loading: false,
            title: 'Введите код MAX',
            description: activationUrl
              ? MAX_REQUIRED_BINDING_DESCRIPTION
              : 'Перейдите в MAX-бота, нажмите «Начать» и введите здесь 6-значный код активации.',
            descriptionLink: activationUrl || null,
            resetCodeKey: Date.now(),
            confirmText: 'Активировать',
            cancelText: 'Отменить',
            blocking: !canDismiss,
            hideCancel: !canDismiss,
            onCancel: () => {},
            onConfirm: async ({ code, setError }: ConfirmPayload) => {
              try {
                const activateResponse = await maxBindingApi.activate({
                  user_id: userId,
                  code,
                })

                if (isError(activateResponse)) {
                  setError(
                    getErrorText(
                      activateResponse,
                      'Некорректный код активации или ошибка сервера',
                    ),
                  )
                  return false
                }

                dispatch(refreshMessengerBindingStatus())
                const freshUser = await fetchCurrentUser()

                if (isEnabledFlag(freshUser?.requireMaxBinding)) {
                  setError('MAX привязан, но ограничение еще не снято. Попробуйте еще раз через несколько секунд.')
                  return false
                }

                if (isEnabledFlag(freshUser?.requireMaxConfirmation)) {
                  activeModeRef.current = 'confirmation'
                  await showLoginConfirmationModalRef.current?.(userId, canDismiss)
                  return false
                }

                activeModeRef.current = null

                return true
              } catch (error) {
                setError(
                  getErrorText(
                    error,
                    'Некорректный код активации или ошибка сервера',
                  ),
                )
                return false
              }
            },
          }),
        )
      } catch (error) {
        dispatch(
          updateConfirm({
            loading: false,
            description: getErrorText(
              error,
              'Не удалось подготовить ссылку для активации MAX. Обновите страницу и попробуйте еще раз.',
            ),
            cancelText: 'Отменить',
            blocking: !canDismiss,
            hideCancel: !canDismiss,
          }),
        )
      }
    },
    [dispatch, fetchCurrentUser],
  )

  const showLoginConfirmationModal = useCallback(
    async (userId: number, canDismiss: boolean) => {
      const confirmLoginCode = async ({ code, setError }: ConfirmPayload) => {
        try {
          const verifyResponse = await API.post(
            'two-factor/verify-code',
            {
              operation: EMPLOYEE_AUTH_OPERATION,
              user_id: userId,
              code,
              force_confirmation: true,
            },
            { skipGlobalErrorAlert: true } as any,
          )

          if (isError(verifyResponse)) {
            setError(
              getErrorText(
                verifyResponse,
                'Не удалось подтвердить вход через MAX',
              ),
            )
            return false
          }

          activeModeRef.current = null
          const freshUser = await fetchCurrentUser()

          if (
            !isEnabledFlag(freshUser?.requireMaxBinding) &&
            !isEnabledFlag(freshUser?.requireMaxConfirmation)
          ) {
            reloadPageAfterMaxLoginConfirmation()
          }

          return true
        } catch (error) {
          setError(
            getErrorText(error, 'Не удалось подтвердить вход через MAX'),
          )
          return false
        }
      }

      dispatch(
        showConfirm({
          variant: 'twoFactor',
          title: 'Подтвердите вход через MAX',
          description: 'Отправляем код подтверждения...',
          codeLength: MAX_CODE_LENGTH,
          confirmText: 'Подтвердить',
          cancelText: 'Отменить',
          loading: true,
          blocking: !canDismiss,
          hideCancel: !canDismiss,
          onCancel: () => {},
          onConfirm: () => false,
        }),
      )

      try {
        const botUrl = await getMaxBotUrlFromConfig().catch(() => null)
        const requestCodeResponse = await API.post(
          'two-factor/request-code',
          {
            operation: EMPLOYEE_AUTH_OPERATION,
            user_id: userId,
            force_confirmation: true,
          },
          { skipGlobalErrorAlert: true } as any,
        )

        if (isError(requestCodeResponse)) throw requestCodeResponse

        dispatch(
          updateConfirm({
            loading: false,
            title: 'Введите код MAX',
            description: MAX_LOGIN_CONFIRMATION_DESCRIPTION,
            descriptionLink: botUrl,
            resetCodeKey: Date.now(),
            confirmText: 'Подтвердить',
            cancelText: 'Отменить',
            blocking: !canDismiss,
            hideCancel: !canDismiss,
            onCancel: () => {},
            onConfirm: confirmLoginCode,
          }),
        )
      } catch (error) {
        dispatch(
          updateConfirm({
            loading: false,
            title: 'Введите код MAX',
            description: getErrorText(
              error,
              'Не удалось отправить код подтверждения MAX. Обновите страницу и попробуйте еще раз.',
            ),
            resetCodeKey: Date.now(),
            confirmText: 'Подтвердить',
            cancelText: 'Отменить',
            blocking: !canDismiss,
            hideCancel: !canDismiss,
            onCancel: () => {},
            onConfirm: confirmLoginCode,
          }),
        )
      }
    },
    [dispatch, fetchCurrentUser],
  )

  showLoginConfirmationModalRef.current = showLoginConfirmationModal

  useEffect(() => {
    if (!profile?.role) return

    if (isMaxHiddenForRole(profile?.role)) {
      activeModeRef.current = null
      setCurrentUser(null)
      return
    }

    activeModeRef.current = null
    setCurrentUser(null)

    fetchCurrentUser().catch((error) => {
      addAlertRef.current({
        title: 'Ошибка',
        text: getErrorText(error, 'Не удалось проверить обязательную привязку MAX'),
        type: 'error',
      })
    })
  }, [fetchCurrentUser, profile?.role])

  useEffect(() => {
    if (!profile?.role) return
    if (isMaxHiddenForRole(profile?.role)) return
    if (!profile?.id || !currentUser) return

    if (isEnabledFlag(currentUser.requireMaxBinding)) {
      if (activeModeRef.current !== 'binding') {
        activeModeRef.current = 'binding'
        void showBindingModal(Number(profile.id), canDismissRequiredMaxModal)
      }
      return
    }

    if (isEnabledFlag(currentUser.requireMaxConfirmation)) {
      if (activeModeRef.current !== 'confirmation') {
        activeModeRef.current = 'confirmation'
        void showLoginConfirmationModal(Number(profile.id), canDismissRequiredMaxModal)
      }
      return
    }

    if (activeModeRef.current) {
      activeModeRef.current = null
      dispatch(hideConfirm())
    }
  }, [
    canDismissRequiredMaxModal,
    currentUser,
    dispatch,
    profile?.id,
    profile?.role,
    showBindingModal,
    showLoginConfirmationModal,
  ])

  return null
}

export default ActivateMaxBot
export { RequiredActivateMaxBot }
